<script setup>
  import { computed, nextTick, onMounted, reactive, ref, useTemplateRef, watch } from "vue";
  import { useElementVisibility } from "@vueuse/core";
  import TargetIcon from "@/assets/svg/targets.svg";

  import { gsap } from "gsap";
  import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";

  gsap.registerPlugin(DrawSVGPlugin);

  const activeIndex = ref(0);
  const interval = ref(null);
  const htmlItems = ref(null);
  const itemsWrapperHeight = ref(300);

  const wrapper = useTemplateRef("wrapper");
  const isVisible = useElementVisibility(wrapper);

  const props = defineProps({
    smallText: String,
    items: String,
  });

  const timelines = reactive({
    one: null,
    two: null,
    three: null,
  });

  const items = computed(() => {
    return JSON.parse(props.items);
  });

  const activeItem = computed(() => {
    const content = {
      index: activeIndex.value,
      text: items.value[activeIndex.value].text,
    };
    return content;
  });

  const setActiveItemOnClick = (index) => {
    // Break the Interval on Click
    clearInterval(interval.value);

    revertAnimation(activeIndex.value);
    activeIndex.value = index;
    timelines[`${items.value[index].target}`].play();

    // Reset Autoplay after 10 Seconds
    setTimeout(() => {
      setActiveItem(activeIndex.value);
      triggerAutoplay();
    }, 15000);
  }

  const setActiveItem = (index) => {
    activeIndex.value = index;
    timelines[`${items.value[index].target}`].play();
  };

  const revertAnimation = (index) => {
    timelines[`${items.value[index].target}`].reverse();
  };


  onMounted(async () => {
    await nextTick();
    await triggerTimelineAnimation();
    setActiveItem(activeIndex.value);

    htmlItems.value = document.querySelectorAll('.targets-text-item')
    await setItemsWrapperHeight()
  });

  const setItemsWrapperHeight = () =>{
    const elementHeight = htmlItems.value[activeIndex.value].offsetHeight
    itemsWrapperHeight.value = elementHeight + 50
  }

  const triggerTimelineAnimation = async () => {
    items.value.forEach((item, index) => {
      if (!item.target) return false
      const timeline = gsap.timeline({
        paused: true,
      });
      timeline.from(`.element-target-groups #${item.target}`, {
        drawSVG: 0,
        duration: 1.5,
        ease: "inOut",
      })
      timelines[`${item.target}`] = timeline
    })
    return true
  }

  watch(isVisible, () => {
    if (isVisible.value) {
      triggerAutoplay();
    }
  });

  const triggerAutoplay = () => {
    interval.value = setInterval(() => {
      revertAnimation(activeIndex.value);
      if (activeIndex.value === items.value.length - 1) {
        setActiveItem(0);
        return
      }
      setActiveItem(activeIndex.value + 1);
    }, 8000)
  }

</script>

<template>
  <section
    ref="wrapper"
    class="element-target-groups bg-secondary ~py-16/28 ~mt-16/28"
  >
    <div class="max-w-screen-2xl flex flex-col lg:flex-row mx-auto px-6">
      <div
        class="targets-icon w-full flex items-center justify-center lg:w-1/2 overflow-hidden"
        v-html="TargetIcon"
      >
      </div>
      <div class="w-full lg:w-1/2 xl:pr-20 mt-12 lg:mt-0">
        <p class="uppercase ~text-xs/sm tracking-wider mb-2 lg:mb-8">
          {{ smallText }}
        </p>
        <div class="targets-navigation">
          <button
            v-for="(item, index) in items"
            :key="index"
            @click="setActiveItemOnClick(index)"
            class="targets-navigation-item w-full flex flex-row items-center relative opacity-60 hover:opacity-100 transition-all duration-500 py-6"
          >
            <span
              class="targets-navigation-item-icon overflow-hidden"
              :class="activeIndex === index ? 'is-active' : ''"
            >
              <AppIcon
                name="ArrowLink"
              />
            </span>
            <span class="~text-2xl/3xl text-headline">
              {{ item.headline }}
            </span>
          </button>
        </div>
        <div
          :style="{ minHeight: `${itemsWrapperHeight}px` }"
          class="targets-text-items relative flex flex-col items-start justify-end ~mt-10/20 w-auto"
        >
          <div
            v-for="(item, index) in items"
            :key="index"
            class="targets-text-item absolute top-0 left-0 transition-all duration-300"
            :class="activeIndex === index ? 'opacity-100' : 'opacity-0'"
            v-html="item.text"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.targets-icon {
  :deep(svg), :deep(path) {
    @apply w-full h-full lg:min-w-[600px];
  }
}
.targets-navigation-item {
  :deep(svg) {
    @apply mr-3;
  }
  :deep(svg path) {
    fill: #3d1314 !important;
  }
  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    width: 100%;
    opacity: 60%;
    background: #3d1314;
  }
  &::after {
    content: "";
    position: absolute;
    transition: width 0.5s ease-in-out;
    bottom: 0;
    left: 0;
    height: 2px;
    width: 0;
    background: #3d1314;
  }
  &:hover {
    &::after {
      width: 100%;
    }
  }
}

.targets-navigation-item-icon {
  @apply w-0 opacity-0;
  transition: width 0.3s ease-in-out 0.3s, opacity 0.3s ease-in-out;
  &.is-active {
    @apply w-8 opacity-100;
    transition: width 0.3s ease-in-out, opacity 0.3s ease-in-out 0.3s;
  }
}

.targets-text-item {
  :deep(p) {
    @apply ~my-4/8;
    line-height: 1.8 !important;
  }
  :deep(h2) {
    @apply ~text-xl/2xl ~mb-4/6;
  }
  :deep(h3) {
    @apply ~text-lg/xl ~mb-4/6;
  }

  :deep(strong) {
    @apply font-bold pb-6;
  }

  :deep(ul li) {
    @apply ~text-sm/base py-1;
    &::before {
      @apply top-3.5 h-1 w-1;
    }
  }
}
</style>
